import {
  SELECTION_ROTATION_HANDLE_OFFSET_CSS_PIXELS,
  selectionRotationHandlePlacement,
  selectionTransformCorners,
} from "./selectionGeometry";
import type {
  IntegerRect,
  RasterSelectionOverlayState,
  RasterSelectionRotationHandleSide,
  RasterSelectionTransform,
} from "./selectionTypes";

const VISUAL_HANDLE_RADIUS_CSS_PIXELS = 14;

function drawTransformFrame(
  context: CanvasRenderingContext2D,
  transform: Readonly<RasterSelectionTransform>,
  zoom: number,
  constraint: Readonly<IntegerRect>,
  lockedRotationHandleSide: RasterSelectionRotationHandleSide | null,
): void {
  const corners = selectionTransformCorners(transform);
  const northWest = corners["resize-north-west"];
  const northEast = corners["resize-north-east"];
  const southEast = corners["resize-south-east"];
  const southWest = corners["resize-south-west"];
  const rotationHandle = selectionRotationHandlePlacement(
    transform,
    zoom,
    constraint,
    lockedRotationHandleSide,
  );
  const radius = VISUAL_HANDLE_RADIUS_CSS_PIXELS / zoom;

  context.beginPath();
  context.moveTo(northWest.x, northWest.y);
  context.lineTo(northEast.x, northEast.y);
  context.lineTo(southEast.x, southEast.y);
  context.lineTo(southWest.x, southWest.y);
  context.closePath();
  context.stroke();

  context.setLineDash([]);
  context.beginPath();
  context.moveTo(rotationHandle.anchor.x, rotationHandle.anchor.y);
  context.lineTo(rotationHandle.point.x, rotationHandle.point.y);
  context.stroke();

  for (const point of [...Object.values(corners), rotationHandle.point]) {
    context.beginPath();
    context.arc(point.x, point.y, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
  }
}

export function renderRasterSelectionOverlay(
  context: CanvasRenderingContext2D,
  state: Readonly<RasterSelectionOverlayState>,
  viewportZoom: number,
): void {
  const zoom = Math.max(0.05, Number.isFinite(viewportZoom) ? viewportZoom : 1);
  context.save();
  context.globalAlpha = 1;
  context.globalCompositeOperation = "source-over";
  context.strokeStyle = "#625bff";
  context.fillStyle = "#ffffff";
  context.lineWidth = 3 / zoom;
  context.setLineDash([12 / zoom, 8 / zoom]);

  if (state.marquee) {
    context.strokeRect(state.marquee.x, state.marquee.y, state.marquee.width, state.marquee.height);
  } else if (state.selection && state.transform) {
    drawTransformFrame(
      context,
      state.transform,
      zoom,
      state.selection.clipRect,
      state.rotationHandleSide,
    );
  }

  context.setLineDash([]);
  context.restore();
}

export const RASTER_SELECTION_ROTATION_HANDLE_OFFSET_CSS_PIXELS =
  SELECTION_ROTATION_HANDLE_OFFSET_CSS_PIXELS;
